'use client';

import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Send as SendIcon,
  LocalShipping as ShippingIcon,
  Scale as ScaleIcon,
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { useTranslation } from '@/kit/useTranslation';

const BUSINESS_COLORS = {
  primary: '#1a1a1a',
  accent: '#f59e0b',
  success: '#10b981',
};

export default function BulkSummary({
  bulkOrder,
  items,
  onCalculate,
  onSubmit,
  isCalculating,
  isSubmitting,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const totalItems = items?.length || 0;
  // Ensure weights are numeric (some items may have weight as string). Coerce with parseFloat and default to 0.
  const totalWeight = Number(
    items?.reduce((sum, item) => sum + (parseFloat(item?.weightKg) || 0), 0) || 0
  );
  const displayTotalWeight = Number.isFinite(totalWeight) ? totalWeight.toFixed(1) : '0.0';
  const subtotal = Number(bulkOrder?.subtotalAmount || 0);
  const discount = Number(bulkOrder?.discountAmount || 0);
  const total = Number(bulkOrder?.totalAmount || subtotal - discount);
  const hasPrice = subtotal > 0;
  const routeSavings = Number(bulkOrder?.routeSavingsPercent || 0);

  // Total savings = route-clustering savings + volume discount savings
  // routeSavings now reflects true savings vs. individual booking (computed in backend)
  const rupeesSavedFromClustering =
    routeSavings > 0 && subtotal > 0 ? Math.round((subtotal + discount) * (routeSavings / 100)) : 0;
  // discountAmount is the volume discount applied on top (3%+ slab)
  const totalRupeesSaved =
    rupeesSavedFromClustering > 0
      ? rupeesSavedFromClustering + Math.round(discount)
      : Math.round(discount);
  const hasSavings = totalRupeesSaved > 0;
  const savingsPercent =
    routeSavings > 0
      ? Math.round(routeSavings)
      : subtotal + discount > 0
        ? Math.round((discount / (subtotal + discount)) * 100)
        : 0;

  // GST = total - (subtotal - discount); total_amount now stores sender price with GST
  const afterDiscount = subtotal - discount;
  const gstAmount = total > afterDiscount ? total - afterDiscount : 0;

  const SummaryItem = ({ icon: Icon, label, value, highlight }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon
          sx={{
            fontSize: 18,
            color: highlight ? BUSINESS_COLORS.accent : '#64748b',
          }}
        />
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: highlight ? BUSINESS_COLORS.primary : '#64748b',
            fontWeight: highlight ? 600 : 400,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: highlight ? '1rem' : '0.875rem',
          fontWeight: highlight ? 700 : 500,
          color: highlight ? BUSINESS_COLORS.success : BUSINESS_COLORS.primary,
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: '#fafafa',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BUSINESS_COLORS.primary }}>
          {t('bulkBooking.orderSummary')}
        </Typography>
      </Box>

      {/* Summary Content */}
      <Box sx={{ p: 3 }}>
        <Stack spacing={0}>
          <SummaryItem
            icon={ShippingIcon}
            label={t('bulkBooking.totalItems')}
            value={`${totalItems} ${totalItems === 1 ? t('bulkBooking.deliverySingular') : t('bulkBooking.deliveryPlural')}`}
          />
          <SummaryItem
            icon={ScaleIcon}
            label={t('bulkBooking.totalWeightLabel')}
            value={`${displayTotalWeight} kg`}
          />
          <Divider sx={{ my: 1.5 }} />

          {hasPrice ? (
            <>
              <SummaryItem
                icon={MoneyIcon}
                label={t('bulkBooking.subtotal')}
                value={`₹${subtotal.toFixed(2)}`}
              />
              {discount > 0 && (
                <SummaryItem
                  icon={PercentIcon}
                  label={t('bulkBooking.bulkDiscount')}
                  value={`−₹${discount.toFixed(2)}`}
                />
              )}
              {gstAmount > 0 && (
                <SummaryItem
                  icon={PercentIcon}
                  label="GST (18%)"
                  value={`₹${gstAmount.toFixed(2)}`}
                />
              )}
              {hasSavings && (
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    borderRadius: 3,
                    bgcolor: alpha('#10b981', 0.1),
                    border: '2px dashed #10b981',
                    position: 'relative',
                    overflow: 'hidden',
                    animation: 'pulse 2s infinite ease-in-out',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        boxShadow: '0 0 0 0px rgba(16, 185, 129, 0.4)',
                      },
                      '50%': {
                        transform: 'scale(1.02)',
                        boxShadow: '0 0 20px 10px rgba(16, 185, 129, 0)',
                      },
                      '100%': {
                        transform: 'scale(1)',
                        boxShadow: '0 0 0 0px rgba(16, 185, 129, 0)',
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 900,
                        color: '#10b981',
                        letterSpacing: -0.5,
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      ₹{totalRupeesSaved}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: '#10b981',
                        fontSize: '1.2rem',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                      }}
                    >
                      Saved!
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      color: '#065f46',
                      fontWeight: 600,
                      textAlign: 'center',
                    }}
                  >
                    {savingsPercent > 0 ? `${savingsPercent}% ` : ''}Bulk Booking Discount Applied
                    🎉
                  </Typography>
                  {/* Decorative Sparkles */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      width: 40,
                      height: 40,
                      bgcolor: '#fbbf24',
                      borderRadius: '50%',
                      opacity: 0.2,
                      filter: 'blur(20px)',
                    }}
                  />
                </Box>
              )}
              <Divider sx={{ my: 1.5 }} />
              <SummaryItem
                icon={MoneyIcon}
                label={t('bulkBooking.totalAmount')}
                value={`₹${total.toFixed(2)}`}
                highlight
              />
              <Box sx={{ mt: 0.5, pl: 3.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                  Includes GST • Pickup address required for final pricing
                </Typography>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                py: 2,
                textAlign: 'center',
                bgcolor: alpha(BUSINESS_COLORS.accent, 0.05),
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#64748b',
                }}
              >
                {t('bulkBooking.clickCalculatePricing')}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Discount note */}
        {discount === 0 && subtotal > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(BUSINESS_COLORS.accent, 0.1),
              border: `1px dashed ${BUSINESS_COLORS.accent}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: BUSINESS_COLORS.primary,
                fontWeight: 500,
              }}
            >
              {t('bulkBooking.bulkDiscountComingSoon')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#fafafa',
        }}
      >
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onCalculate}
            disabled={isCalculating || totalItems === 0}
            startIcon={
              isCalculating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: BUSINESS_COLORS.primary,
              color: BUSINESS_COLORS.primary,
              px: 3,
              '&:hover': {
                borderColor: BUSINESS_COLORS.primary,
                bgcolor: alpha(BUSINESS_COLORS.primary, 0.05),
              },
            }}
          >
            {isCalculating ? t('bulkBooking.calculating') : t('bulkBooking.calculatePricing')}
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitting || totalItems === 0 || !hasPrice}
            startIcon={
              isSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon />
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: BUSINESS_COLORS.accent,
              px: 4,
              py: 1.25,
              '&:hover': { bgcolor: '#d97706' },
              '&:disabled': {
                bgcolor: '#e2e8f0',
                color: '#94a3b8',
              },
            }}
          >
            {isSubmitting ? t('bulkBooking.submitting') : t('bulkBooking.submitOrder')}
          </Button>
        </Stack>

        {!hasPrice && totalItems > 0 && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#94a3b8',
              textAlign: 'right',
              mt: 1,
            }}
          >
            {t('bulkBooking.calculateBeforeSubmitting')}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
